/**
 * Loader for the `almanak.phenomena` table (curated phenomenon pages).
 *
 * Moves the former hardcoded `PHENOMENA` array out of phenomenon-pages.tsx into
 * a DB table so pages can be admin-edited and pipeline-enriched. Follows the
 * preload → sync-getter pattern used by db-supabase.ts: server entry points
 * (layout, sitemap, page metadata) must `await preloadPhenomena()` before any
 * sync reader (getPhenomenaRows) runs. Backed by unstable_cache cross-request;
 * a Promise singleton dedupes concurrent in-process callers.
 *
 * The table is not in the generated database.types.ts (it is admin-managed and
 * regenerated manually), so rows are fetched untyped and shaped here. meta_title
 * / meta_description carry {year} / {site} placeholders resolved by the caller
 * (phenomenon-pages.tsx) against CALENDAR_YEAR / SITE_NAME.
 *
 * Refs: almanak-pju1, almanak-pju1.2
 */
import { createClient } from '@supabase/supabase-js';
import { unstable_cache } from 'next/cache';
import { fetchAllUntyped } from './db-helpers';
import type { Database } from './database.types';

/** Raw shape of an almanak.phenomena row (snake_case, jsonb columns typed loosely). */
export interface PhenomenaRow {
  id: string;
  title: string;
  country_code: string;
  event_matcher: unknown;
  meta_title: string;
  meta_description: string;
  highlights: unknown;
  venues: unknown;
  google_maps_url: string | null;
  sort_order: number;
  status: string;
}

const url = process.env.NEXT_PUBLIC_SUPABASE_URL;
const key = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
if (!url || !key) {
  throw new Error('Missing NEXT_PUBLIC_SUPABASE_URL or NEXT_PUBLIC_SUPABASE_ANON_KEY');
}
const sb = createClient<Database, 'almanak'>(url, key, { db: { schema: 'almanak' } });

const _cachedPhenomenaRaw = unstable_cache(
  async () => fetchAllUntyped(sb, 'phenomena'),
  ['db-phenomena:phenomena'],
  { revalidate: 3600, tags: ['phenomena'] },
);

let _phenomena: PhenomenaRow[] | null = null;
let _preloadPromise: Promise<void> | null = null;

async function _doPreload(): Promise<void> {
  const rows = await _cachedPhenomenaRaw();
  _phenomena = (rows as unknown as PhenomenaRow[])
    .filter(r => r.status === 'published')
    .sort((a, b) => a.sort_order - b.sort_order);
}

/** Preload curated phenomena. Idempotent Promise singleton. */
export function preloadPhenomena(): Promise<void> {
  if (!_preloadPromise) {
    _preloadPromise = _doPreload().catch(err => {
      _preloadPromise = null;
      throw err;
    });
  }
  return _preloadPromise;
}

/** True once the phenomena cache is populated (gates the evergreen registry). */
export function isPhenomenaLoaded(): boolean {
  return _phenomena !== null;
}

/** Published curated phenomena rows, sort_order ascending. Empty until preloaded. */
export function getPhenomenaRows(): PhenomenaRow[] {
  return _phenomena ?? [];
}

/** Test-only: clear the preload singleton so a fresh preloadPhenomena() re-fetches. */
export function _resetPreloadPhenomenaForTesting(): void {
  _preloadPromise = null;
  _phenomena = null;
}
